import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Trophy, Inbox, Check, X, ArrowRight, Users } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { categoryLabel, PAYMENT_STATUS_META } from "@/lib/tournament";
import api from "@/lib/api";

export default function MyTournaments() {
  const nav = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/my/registrations").then(({ data }) => setData(data)).catch(() => setData(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const respond = async (rid, accept) => {
    try {
      await api.post(`/registrations/${rid}/partner-response`, { accept });
      toast.success(accept ? "Invite accepted" : "Invite declined");
      load();
    } catch (e) { toast.error("Could not respond"); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="My Tournaments" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 grain">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c">Player hub</span>
          <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-2">My Tournaments</h1>
        </motion.div>

        {data === null && <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>}
        {data === false && <p className="mt-8 text-sm" style={{ color: "var(--text-2)" }}>Could not load your registrations.</p>}

        {data && data !== false && (
          <>
            {/* partner invites */}
            {data.invites.length > 0 && (
              <div className="mt-8" data-testid="partner-invites">
                <h3 className="font-display font-bold flex items-center gap-2 mb-3"><Inbox className="w-4 h-4 text-primary-c" /> Partner invites</h3>
                <div className="space-y-2">
                  {data.invites.map((r) => (
                    <div key={r.registration_id} data-testid={`invite-${r.registration_id}`} className="rounded-2xl glow-border p-4 flex items-center justify-between gap-3 flex-wrap" style={{ background: "var(--surface)" }}>
                      <div>
                        <div className="font-bold text-sm flex items-center gap-2"><Users className="w-4 h-4 text-primary-c" /> {r.player_name} invited you</div>
                        <div className="text-xs mt-0.5" style={{ color: "var(--text-2)" }}>{r.tournament_name} · {r.category_label}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button data-testid={`invite-accept-${r.registration_id}`} onClick={() => respond(r.registration_id, true)} className="btn-glow rounded-full px-4 py-2 text-sm font-bold flex items-center gap-1.5"><Check className="w-4 h-4" /> Accept</button>
                        <button data-testid={`invite-decline-${r.registration_id}`} onClick={() => respond(r.registration_id, false)} className="glass rounded-full px-4 py-2 text-sm font-bold flex items-center gap-1.5" style={{ color: "var(--text)" }}><X className="w-4 h-4" /> Decline</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* my registrations */}
            <div className="mt-8">
              <h3 className="font-display font-bold flex items-center gap-2 mb-3"><Trophy className="w-4 h-4 text-primary-c" /> My registrations</h3>
              {data.registrations.length === 0 ? (
                <div className="rounded-2xl glow-border p-10 text-center" style={{ background: "var(--surface)" }} data-testid="my-regs-empty">
                  <Trophy className="w-8 h-8 mx-auto text-primary-c opacity-60" />
                  <p className="mt-3 text-sm" style={{ color: "var(--text-2)" }}>You haven't registered for any tournaments yet. Open a registration link from an organizer to sign up.</p>
                </div>
              ) : (
                <div className="space-y-2" data-testid="my-regs-list">
                  {data.registrations.map((r) => {
                    const pm = PAYMENT_STATUS_META[r.payment_status] || PAYMENT_STATUS_META.pending;
                    return (
                      <button key={r.registration_id} data-testid={`my-reg-${r.registration_id}`} onClick={() => nav(`/t/${r.tournament_id}`)}
                        className="w-full text-left rounded-2xl glow-border p-4 flex items-center justify-between gap-3 hover:scale-[1.005] transition-transform" style={{ background: "var(--surface)" }}>
                        <div>
                          <div className="font-bold text-sm">{r.tournament_name}</div>
                          <div className="text-xs mt-0.5" style={{ color: "var(--text-2)" }}>{r.category_label}
                            {r.discipline === "doubles" && r.partner && <span> · partner {r.partner.status === "accepted" ? `✓ ${r.partner.name}` : r.partner.status === "declined" ? "declined" : "pending"}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="mono text-[11px] uppercase tracking-wider glass rounded-full px-2.5 py-1" style={{ color: pm.color }}>{pm.l}</span>
                          <ArrowRight className="w-4 h-4 text-primary-c" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
